// src/pages/settings/NotificationsPage.tsx — DEM-2043
//
// Central de notificações push do RAIOS — /settings/notifications.
//
//   1. PERMISSÃO push (desktop + PWA instalado): ativar/desativar. Persiste a
//      subscription deste device no backend (upsert idempotente por endpoint).
//   2. 3 CONTROLES INDEPENDENTES de preferência:
//        - Demanda · status macro      (off / só ao finalizar / a cada mudança)
//        - Demanda · etapa do pipeline  (off / a cada mudança)        ← sem "só ao finalizar"
//        - Skill                        (off / só ao finalizar / a cada mudança)
//      Ligar um eixo NÃO altera os outros.
//
// iOS: push só funciona com o PWA instalado na tela inicial (16.4+). A UI mostra
// a instrução quando detecta iOS sem instalar.

import { useEffect, useState, useCallback } from 'react';
import { api, type NotificationPreferences } from '@/lib/api';
import {
  enablePush,
  disablePush,
  getPermission,
  isPushSupported,
  isIosNeedsInstall,
  hasActiveSubscription,
  type PushPermission,
} from '@/lib/push-notifications';
import { Bell, BellOff, Loader2, Smartphone } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { Alert } from '@/components/ui/Alert';
import { Skeleton } from '@/components/ui/Skeleton';
import { useToast } from '@/components/ui/Toast';

const LEVEL_3 = [
  { value: 'off', label: 'Desligado' },
  { value: 'terminal_only', label: 'Só ao finalizar' },
  { value: 'every_change', label: 'A cada mudança' },
];
const LEVEL_2 = [
  { value: 'off', label: 'Desligado' },
  { value: 'every_change', label: 'A cada mudança' },
];

export function NotificationsPage() {
  const [permission, setPermission] = useState<PushPermission>('default');
  const [subscribed, setSubscribed] = useState(false);
  const [prefs, setPrefs] = useState<NotificationPreferences | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [savingPrefs, setSavingPrefs] = useState(false);
  const { toast } = useToast();
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setPermission(getPermission());
      setSubscribed(await hasActiveSubscription());
      const res = await api.getNotificationPreferences();
      setPrefs(res.preferences);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao carregar preferências');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function handleEnable() {
    setBusy(true);
    setError(null);
    try {
      const result = await enablePush();
      setPermission(result);
      if (result === 'granted') setSubscribed(true);
      else if (result === 'denied') {
        setError('Permissão negada. Habilite as notificações nas configurações do navegador.');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao ativar notificações');
    } finally {
      setBusy(false);
    }
  }

  async function handleDisable() {
    setBusy(true);
    setError(null);
    try {
      await disablePush();
      setSubscribed(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao desativar notificações');
    } finally {
      setBusy(false);
    }
  }

  async function handleTest() {
    setTesting(true);
    try {
      await api.testPushNotification();
      toast({ message: 'Notificação de teste enviada — confira o aviso do navegador.', type: 'success' });
    } catch (e) {
      toast({ message: e instanceof Error ? e.message : 'Não foi possível enviar o teste.', type: 'error' });
    } finally {
      setTesting(false);
    }
  }

  async function updatePref(patch: Partial<NotificationPreferences>) {
    if (!prefs) return;
    const next = { ...prefs, ...patch };
    setPrefs(next); // otimista — eixos independentes, patch só toca um campo
    setSavingPrefs(true);
    setError(null);
    try {
      const res = await api.updateNotificationPreferences(next);
      setPrefs(res.preferences);
      toast({ message: 'Preferência de notificação salva.', type: 'success' });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao salvar preferência');
      toast({ message: 'Não foi possível salvar a preferência.', type: 'error' });
      await refresh();
    } finally {
      setSavingPrefs(false);
    }
  }

  const pushSupported = isPushSupported();
  const iosNeedsInstall = isIosNeedsInstall();

  return (
    <div className="space-y-6">
      <PageHeader title="Notificações" description="Notificações push do RAIOS no desktop e no PWA instalado." />

      {error && <Alert variant="destructive">{error}</Alert>}

      {/* ─── Permissão push ─── */}
      <Card className="p-5 space-y-4">
        <div className="flex items-start gap-3">
          {subscribed ? (
            <Bell className="h-5 w-5 text-raios-accent-text mt-0.5" />
          ) : (
            <BellOff className="h-5 w-5 text-raios-muted mt-0.5" />
          )}
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-raios-text">Permissão push neste dispositivo</h3>
            <p className="text-xs text-raios-muted mt-0.5">
              {subscribed
                ? 'Ativado — este dispositivo recebe notificações push.'
                : 'Ative para receber notificações push neste dispositivo.'}
            </p>
          </div>
        </div>

        {iosNeedsInstall && (
          <Alert variant="info">
            <div className="flex items-center gap-2">
              <Smartphone className="h-4 w-4" />
              <span>
                No iPhone/iPad, instale o RAIOS na tela inicial (Compartilhar → Adicionar à Tela de Início) para
                receber notificações push (iOS 16.4+).
              </span>
            </div>
          </Alert>
        )}

        {!pushSupported && !iosNeedsInstall && (
          <Alert variant="warning">Este navegador não suporta Web Push.</Alert>
        )}

        {pushSupported && (
          <div>
            {subscribed ? (
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" onClick={handleDisable} disabled={busy || testing}>
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <BellOff className="h-4 w-4" />}
                  Desativar notificação
                </Button>
                <Button variant="brand" onClick={handleTest} disabled={busy || testing}>
                  {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Bell className="h-4 w-4" />}
                  Testar notificação
                </Button>
              </div>
            ) : (
              <Button variant="brand" onClick={handleEnable} disabled={busy || permission === 'denied'}>
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Bell className="h-4 w-4" />}
                Ativar notificação
              </Button>
            )}
            {permission === 'denied' && (
              <p className="text-xs text-raios-danger-text mt-2">
                Permissão bloqueada no navegador — desbloqueie nas configurações do site.
              </p>
            )}
          </div>
        )}
      </Card>

      {/* ─── 3 controles independentes ─── */}
      <Card className="p-5 space-y-5">
        <div>
          <h3 className="text-sm font-semibold text-raios-text">O que notificar</h3>
          <p className="text-xs text-raios-muted mt-0.5">
            Os três controles são independentes — ligar um não altera os outros.
          </p>
        </div>

        {loading || !prefs ? (
          <div className="space-y-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : (
          <div className="space-y-5">
            <PrefRow
              title="Demanda · status"
              description="Status macro da demanda (planned → running → review → done…)."
              options={LEVEL_3}
              value={prefs.demand_status_level}
              disabled={savingPrefs}
              onChange={(v) => updatePref({ demand_status_level: v as NotificationPreferences['demand_status_level'] })}
            />
            <PrefRow
              title="Demanda · etapa do pipeline"
              description="Etapa da run atrelada (dispatch, consensus, development, peer-review…)."
              options={LEVEL_2}
              value={prefs.demand_pipeline_step_level}
              disabled={savingPrefs}
              onChange={(v) =>
                updatePref({
                  demand_pipeline_step_level: v as NotificationPreferences['demand_pipeline_step_level'],
                })
              }
            />
            <PrefRow
              title="Skill"
              description="Execução de skill (terminou / mudou de estado)."
              options={LEVEL_3}
              value={prefs.skill_level}
              disabled={savingPrefs}
              onChange={(v) => updatePref({ skill_level: v as NotificationPreferences['skill_level'] })}
            />
          </div>
        )}
      </Card>
    </div>
  );
}

interface PrefRowProps {
  title: string;
  description: string;
  options: { value: string; label: string }[];
  value: string;
  disabled: boolean;
  onChange: (value: string) => void;
}

function PrefRow({ title, description, options, value, disabled, onChange }: PrefRowProps) {
  return (
    <div className="flex items-center justify-between gap-4">
      <div className="flex-1">
        <p className="text-sm font-medium text-raios-text">{title}</p>
        <p className="text-xs text-raios-muted mt-0.5">{description}</p>
      </div>
      <div className="w-48 shrink-0">
        <Select options={options} value={value} onChange={onChange} disabled={disabled} />
      </div>
    </div>
  );
}

export default NotificationsPage;
